import type { IdVidro } from '../didatico/manejoVidros'
import { perfilVeiculoPorId, type PerfilVeiculo } from '../didatico/perfilVeiculo'
import { atualizarObjeto } from './operacoes'
import type {
  BlocoVeiculoTabletop,
  CenaTabletop,
  DescritorCorteTabletop,
  DescritorDeformacaoTabletop,
  EstadoCortePecaTabletop,
  IdPortaTabletop,
} from './tipos'
import type { NivelDanoVeicular, RegiaoDanoVeicular } from '../didatico/danoVeicular'
import type { CampoDeformacao, EstadoDeformacaoVeiculo } from '../didatico/campoDeformacao'

export interface ResultadoIntervencaoVeiculoTabletop {
  readonly sucesso: boolean
  readonly mensagem: string
}

export interface RetornoExecucaoCorteTabletop {
  readonly cena: CenaTabletop
  readonly resultado: ResultadoIntervencaoVeiculoTabletop
}

/**
 * Validação contextual de intervenção estrutural ou de corte no veículo.
 * A liberdade de posicionamento no Table top não autoriza ações arbitrárias
 * sem verificação das condições de segurança didática (vidros, sustentação e integridade).
 */
export function validarIntervencaoPeca(
  veiculo: BlocoVeiculoTabletop,
  idPeca: string,
  acao: EstadoCortePecaTabletop,
): ResultadoIntervencaoVeiculoTabletop {
  // Verificação de portas
  if (idPeca.startsWith('porta')) {
    const idVidro = idPeca as IdVidro
    const vidro = veiculo.vidros[idVidro] ?? 'intacto'
    if (acao === 'removida' && vidro === 'intacto') {
      return {
        sucesso: true,
        mensagem: 'Atenção: A porta foi removida com vidro intacto. No protocolo padrão, gerencie o vidro antes da retirada para evitar estilhaços.',
      }
    }
    return {
      sucesso: true,
      mensagem: acao === 'removida' ? 'Porta retirada com sucesso.' : 'Porta aberta.',
    }
  }

  // Verificação de colunas estruturais
  if (idPeca.startsWith('coluna')) {
    const ehEsquerda = idPeca.includes('Esquerda')
    const vidroDianteiro = veiculo.vidros[ehEsquerda ? 'portaDianteiraEsquerda' : 'portaDianteiraDireita'] ?? 'intacto'
    const vidroTraseiro = veiculo.vidros[ehEsquerda ? 'portaTraseiraEsquerda' : 'portaTraseiraDireita'] ?? 'intacto'

    if ((vidroDianteiro === 'intacto' || vidroTraseiro === 'intacto') && acao === 'removida') {
      return {
        sucesso: true,
        mensagem: 'Atenção: Coluna cortada com vidros adjacentes intactos. Recomenda-se quebrar ou remover os vidros antes do corte estrutural.',
      }
    }

    return {
      sucesso: true,
      mensagem: acao === 'removida' ? 'Coluna seccionada e removida da estrutura.' : `Ação ${acao} na coluna.`,
    }
  }

  // Teto
  if (idPeca === 'teto') {
    return {
      sucesso: true,
      mensagem: acao === 'removida' ? 'Teto totalmente retirado.' : 'Teto rebatido para trás.',
    }
  }

  return {
    sucesso: true,
    mensagem: `Ação ${acao} aplicada à peça ${idPeca}.`,
  }
}

/**
 * Aplica um descritor de deformação específico em um veículo do Table top.
 * Preserva o isolamento: outros veículos na mesma cena não sofrem alteração.
 */
export function aplicarDeformacaoNoVeiculo(
  cena: CenaTabletop,
  idVeiculo: string,
  deformacao: DescritorDeformacaoTabletop | null,
): CenaTabletop {
  const veiculoObj = cena.objetos.find((o) => o.id === idVeiculo)
  if (!veiculoObj?.veiculo) return cena

  const danoAtualizado = deformacao
    ? {
        regiao: deformacao.regiao,
        nivel: deformacao.nivel,
        intrusaoM: deformacao.intrusaoM,
      }
    : undefined

  return atualizarObjeto(cena, idVeiculo, {
    veiculo: {
      ...veiculoObj.veiculo,
      deformacao,
      deformacoes: undefined,
      dano: danoAtualizado,
    },
  })
}

/** Aplica ou retira um amassado numa região, sem apagar as outras regiões. */
export function aplicarDeformacaoNaRegiao(
  cena: CenaTabletop,
  idVeiculo: string,
  regiao: RegiaoDanoVeicular,
  deformacao: DescritorDeformacaoTabletop | null,
  idPorta?: IdPortaTabletop,
): CenaTabletop {
  const objeto = cena.objetos.find((o) => o.id === idVeiculo)
  if (!objeto?.veiculo) return cena
  const atuais = objeto.veiculo.deformacoes ?? (objeto.veiculo.deformacao ? [objeto.veiculo.deformacao] : [])
  const deformacoes = atuais.filter((item) => item.idAmassado || item.regiao !== regiao || item.idPorta !== idPorta)
  if (deformacao) deformacoes.push({ ...deformacao, regiao, ...(idPorta ? { idPorta } : {}) })
  const principal = deformacoes[deformacoes.length - 1]
  return atualizarObjeto(cena, idVeiculo, {
    veiculo: {
      ...objeto.veiculo,
      deformacao: null,
      deformacoes,
      dano: principal ? { regiao: principal.regiao, nivel: principal.nivel, intrusaoM: principal.intrusaoM } : undefined,
    },
  })
}

/** Centro e alcance do amassado sobre uma porta concreta, no referencial do veículo. */
export function deformacaoPredefinidaNaPorta(
  perfil: PerfilVeiculo,
  idPorta: IdPortaTabletop,
  nivel: NivelDanoVeicular,
  intrusaoM?: number,
): DescritorDeformacaoTabletop {
  const faixa = idPorta.startsWith('portaDianteira') ? perfil.portaDianteira : perfil.portaTraseira
  return {
    ...calcularDeformacaoFrontalPredefinida(nivel),
    regiao: idPorta.endsWith('Esquerda') ? 'lateralEsquerda' : 'lateralDireita',
    idPorta,
    centroFaceM: (faixa.zDianteiro + faixa.zTraseiro) / 2,
    larguraM: Math.min(0.72, (faixa.zDianteiro - faixa.zTraseiro) * 0.68),
    alturaContatoY: (perfil.topoSoleiraY + perfil.cinturaY) / 2,
    ...(intrusaoM !== undefined ? { intrusaoM } : {}),
  }
}

/** Escolhe a face a partir de um toque no referencial do carro, sem aceitar vidro/roda/solo. */
export function alvoAmassadoNoPonto(
  perfil: PerfilVeiculo,
  ponto: Readonly<{ x: number; y: number; z: number }>,
): Pick<DescritorDeformacaoTabletop, 'regiao' | 'idPorta' | 'centroFaceM' | 'centroLongitudinalM' | 'alturaContatoY'> | null {
  const { x, y, z } = ponto
  if (![x, y, z].every(Number.isFinite)) return null
  if (y >= perfil.tetoY - 0.16 && y <= perfil.tetoY + 0.18 &&
    z >= perfil.teto.zTraseiro && z <= perfil.teto.zDianteiro &&
    Math.abs(x) <= perfil.largura / 2) {
    return { regiao: 'teto', centroFaceM: x, centroLongitudinalM: z, alturaContatoY: y }
  }
  if (Math.abs(x) >= perfil.largura / 2 - 0.18 &&
    Math.abs(x) <= perfil.largura / 2 + 0.12 &&
    y >= perfil.topoSoleiraY - 0.02 && y <= perfil.cinturaY + 0.05 &&
    z >= perfil.traseiraZ && z <= perfil.frenteZ) {
    const esquerda = x > 0
    const faixa = z >= perfil.portaDianteira.zTraseiro && z <= perfil.portaDianteira.zDianteiro
      ? 'Dianteira'
      : z >= perfil.portaTraseira.zTraseiro && z <= perfil.portaTraseira.zDianteiro
        ? 'Traseira'
        : null
    const idPorta = faixa ? `porta${faixa}${esquerda ? 'Esquerda' : 'Direita'}` as IdPortaTabletop : undefined
    return { regiao: esquerda ? 'lateralEsquerda' : 'lateralDireita',
      ...(idPorta ? { idPorta } : {}), centroFaceM: z, alturaContatoY: y }
  }
  if (Math.abs(x) > perfil.largura / 2 + 0.12 || y < 0.25) return null
  if (z >= perfil.colunaA.zBase && z <= perfil.frenteZ + 0.08 &&
    y <= perfil.capoY + 0.16) {
    return { regiao: 'frente', centroFaceM: x, alturaContatoY: y }
  }
  if (z <= perfil.colunaC.zBase && z >= perfil.traseiraZ - 0.08 &&
    y <= perfil.cinturaY + 0.16) {
    return { regiao: 'traseira', centroFaceM: x, alturaContatoY: y }
  }
  return null
}

/** Um clique corresponde a um amassado e a uma entrada no histórico. */
export function adicionarAmassadoNoPonto(
  cena: CenaTabletop,
  idVeiculo: string,
  ponto: Readonly<{ x: number; y: number; z: number }>,
  larguraM: number,
  intrusaoM: number,
  gerarId: () => string = () => globalThis.crypto?.randomUUID?.() ??
    `amassado-${Date.now()}-${Math.random().toString(36).slice(2)}`,
): CenaTabletop {
  const objeto = cena.objetos.find((o) => o.id === idVeiculo)
  if (!objeto?.veiculo || !Number.isFinite(larguraM) || !Number.isFinite(intrusaoM)) return cena
  const perfil = perfilVeiculoPorId(objeto.veiculo.idPerfil)
  const alvo = alvoAmassadoNoPonto(perfil, ponto)
  if (!alvo) return cena
  const intensidade = Math.max(0.08, Math.min(0.45, intrusaoM))
  const novo: DescritorDeformacaoTabletop = {
    ...alvo,
    idAmassado: gerarId(),
    larguraM: Math.max(0.25, Math.min(1.2, larguraM)),
    intrusaoM: intensidade,
    nivel: intensidade >= 0.32 ? 'severo' : intensidade >= 0.19 ? 'moderado' : 'leve',
  }
  const anteriores = objeto.veiculo.deformacoes ?? (objeto.veiculo.deformacao ? [objeto.veiculo.deformacao] : [])
  if (anteriores.some((d) => d.idAmassado === novo.idAmassado)) return cena
  return atualizarObjeto(cena, idVeiculo, {
    veiculo: {
      ...objeto.veiculo,
      deformacao: null,
      deformacoes: [...anteriores, novo],
      dano: { regiao: novo.regiao, nivel: novo.nivel, intrusaoM: novo.intrusaoM },
    },
  })
}

export function limparAmassadosDoVeiculo(cena: CenaTabletop, idVeiculo: string): CenaTabletop {
  const objeto = cena.objetos.find((o) => o.id === idVeiculo)
  if (!objeto?.veiculo || (!objeto.veiculo.deformacao && !objeto.veiculo.deformacoes?.length)) return cena
  return atualizarObjeto(cena, idVeiculo, {
    veiculo: { ...objeto.veiculo, deformacao: null, deformacoes: [], dano: undefined },
  })
}

/** Converte os descritores persistidos para os campos já usados pela carroceria 3D. */
export function estadoVisualDeformacoesTabletop(idVeiculo: string, veiculo: BlocoVeiculoTabletop): EstadoDeformacaoVeiculo {
  const deformacoes = veiculo.deformacoes ?? (veiculo.deformacao ? [veiculo.deformacao] : [])
  return {
    veiculoId: idVeiculo,
    chave: JSON.stringify(deformacoes),
    campos: deformacoes.map((d): CampoDeformacao => ({
      regiao: d.regiao,
      alvo: 'muro',
      variante: 'impacto',
      nivel: d.nivel,
      intrusaoM: d.intrusaoM,
      intrusaoMaxM: d.intrusaoM,
      progresso: 1,
      larguraM: d.larguraM ?? 1.2,
      centroFaceM: d.centroFaceM ?? 0,
      ...(d.idAmassado ? { localizado: true, centroLongitudinalM: d.centroLongitudinalM ?? 0 } : {}),
      alturaContatoY: d.alturaContatoY ?? (d.regiao === 'teto' ? 1.3 : 0.52),
      sinalLateral: d.regiao === 'lateralEsquerda' ? 1 : -1,
      smallOverlap: false,
      assimetria: 0,
      riscos: false,
    })),
  }
}

/**
 * Calcula um descritor frontal predefinido por nível.
 */
export function calcularDeformacaoFrontalPredefinida(
  nivel: NivelDanoVeicular,
): DescritorDeformacaoTabletop {
  switch (nivel) {
    case 'leve':
      return { regiao: 'frente', nivel: 'leve', intrusaoM: 0.12, larguraM: 1.1 }
    case 'moderado':
      return { regiao: 'frente', nivel: 'moderado', intrusaoM: 0.24, larguraM: 1.4 }
    case 'severo':
      return { regiao: 'frente', nivel: 'severo', intrusaoM: 0.38, larguraM: 1.6 }
  }
}

/**
 * Executa uma ação estrutural ou de corte sobre uma peça específica de um veículo do Table top.
 */
export function executarCorteNoVeiculo(
  cena: CenaTabletop,
  idVeiculo: string,
  idPeca: string,
  novoEstado: EstadoCortePecaTabletop,
): RetornoExecucaoCorteTabletop {
  const veiculoObj = cena.objetos.find((o) => o.id === idVeiculo)
  if (!veiculoObj?.veiculo) {
    return {
      cena,
      resultado: { sucesso: false, mensagem: 'Veículo não encontrado.' },
    }
  }

  const validacao = validarIntervencaoPeca(veiculoObj.veiculo, idPeca, novoEstado)

  const cortesAtuais = veiculoObj.veiculo.cortes ?? []
  const corteExistente = cortesAtuais.findIndex((c) => c.idPeca === idPeca)
  const novoCorte: DescritorCorteTabletop = { idPeca, estado: novoEstado }

  const novosCortes = corteExistente >= 0
    ? cortesAtuais.map((c, i) => (i === corteExistente ? novoCorte : c))
    : [...cortesAtuais, novoCorte]

  const novosEstadosPecas = {
    ...veiculoObj.veiculo.estadosPecas,
    [idPeca]: novoEstado,
  }

  const novaCena = atualizarObjeto(cena, idVeiculo, {
    veiculo: {
      ...veiculoObj.veiculo,
      estadosPecas: novosEstadosPecas,
      cortes: novosCortes,
    },
  })

  return { cena: novaCena, resultado: validacao }
}

/**
 * Seleciona semanticamente uma peça específica dentro de um veículo no Table top.
 */
export function selecionarPecaNoVeiculo(
  cena: CenaTabletop,
  idVeiculo: string,
  idPeca: string | null,
): CenaTabletop {
  const veiculoObj = cena.objetos.find((o) => o.id === idVeiculo)
  if (!veiculoObj?.veiculo) return cena

  return atualizarObjeto(cena, idVeiculo, {
    veiculo: {
      ...veiculoObj.veiculo,
      pecaSelecionada: idPeca,
    },
  })
}

/**
 * Restaura toda a estrutura física e cortes de um veículo, preservando vidros e posição.
 */
export function restaurarEstruturaDoVeiculo(cena: CenaTabletop, idVeiculo: string): CenaTabletop {
  const veiculoObj = cena.objetos.find((o) => o.id === idVeiculo)
  if (!veiculoObj?.veiculo) return cena

  return atualizarObjeto(cena, idVeiculo, {
    veiculo: {
      ...veiculoObj.veiculo,
      estadosPecas: {},
      cortes: [],
      deformacao: null,
      deformacoes: [],
      dano: undefined,
      pecaSelecionada: null,
    },
  })
}
